import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ServiceBus } from '@spaces-ui/service-bus';
import { SystemUtils } from '@spaces-ui/utils';

import { GlobalShortcutBusEventPayloads, GlobalShortcutBusEvents, SHORTCUTS } from '../models';

@Injectable({ providedIn: 'root' })
export class GlobalShortcutService {
  private serviceBus = inject(ServiceBus);
  private router = inject(Router);

  private readonly shortcutMap: Map<string, string> = new Map();

  public constructor() {
    for (const s of SHORTCUTS) {
      this.shortcutMap.set(this.normalizeKeys(s.browserKeys), s.id);
    }
  }

  public listenforShortcuts(): void {
    window.addEventListener('keydown', this.onKeyDown, true);
  }

  public getAccelerator(id: string, isElectronApp: boolean, isMacElectronApp: boolean): string {
    const shortcut = SHORTCUTS.find(s => s.id === id);
    if (!shortcut) {
      return '';
    }

    const isMac = isElectronApp ? isMacElectronApp : this.isMac();

    const keys = isElectronApp ? shortcut.keys : shortcut.browserKeys;

    return SystemUtils.formatKeyboardShortcut(keys, isMac);
  }

  public openCreateChannelDialog() {
    this.serviceBus.publish<GlobalShortcutBusEvents.CreateChannel, GlobalShortcutBusEventPayloads>(
      GlobalShortcutBusEvents.CreateChannel,
      undefined,
    );
  }

  public goToFind() {
    this.serviceBus.publish<GlobalShortcutBusEvents.Find, GlobalShortcutBusEventPayloads>(
      GlobalShortcutBusEvents.Find,
      undefined,
    );
  }

  public openCreateDMDialog() {
    this.serviceBus.publish<GlobalShortcutBusEvents.CreateDM, GlobalShortcutBusEventPayloads>(
      GlobalShortcutBusEvents.CreateDM,
      undefined,
    );
  }

  public goTo() {
    this.serviceBus.publish<GlobalShortcutBusEvents.GoTo, GlobalShortcutBusEventPayloads>(
      GlobalShortcutBusEvents.GoTo,
      undefined,
    );
  }

  public openAllOrganizations() {
    this.serviceBus.publish<
      GlobalShortcutBusEvents.AllOrganizations,
      GlobalShortcutBusEventPayloads
    >(GlobalShortcutBusEvents.AllOrganizations, undefined);
  }

  public openDocumentation() {
    this.serviceBus.publish<
      GlobalShortcutBusEvents.OpenDocumentation,
      GlobalShortcutBusEventPayloads
    >(GlobalShortcutBusEvents.OpenDocumentation, undefined);
  }

  public openTodos() {
    this.serviceBus.publish<GlobalShortcutBusEvents.Todos, GlobalShortcutBusEventPayloads>(
      GlobalShortcutBusEvents.Todos,
      undefined,
    );
  }

  // Intentionally has no SHORTCUTS entry — Shift+Esc is Electron-only (it would
  // collide with Chrome's task manager shortcut in the browser build).
  public markAllRead() {
    this.serviceBus.publish<GlobalShortcutBusEvents.MarkAllRead, GlobalShortcutBusEventPayloads>(
      GlobalShortcutBusEvents.MarkAllRead,
      undefined,
    );
  }

  private normalizeKeys(accelerator: string): string {
    return accelerator
      .toLowerCase()
      .replace('cmdorctrl', this.isMac() ? 'meta' : 'control')
      .split('+')
      .sort()
      .join('+');
  }

  private onKeyDown = (e: KeyboardEvent) => {
    const keys = [
      e.ctrlKey && 'control',
      e.metaKey && 'meta',
      e.shiftKey && 'shift',
      e.altKey && 'alt',
      e.key.toLowerCase(),
    ]
      .filter(Boolean)
      .sort()
      .join('+');

    const action = this.shortcutMap.get(keys);
    if (action) {
      e.preventDefault();
      this.handleShortcut(action);
    }
  };

  private handleShortcut(id: string) {
    switch (id) {
      case 'createChannel': {
        this.openCreateChannelDialog();
        break;
      }
      case 'createMessage': {
        this.openCreateDMDialog();
        break;
      }
      case 'allOrganizations': {
        this.openAllOrganizations();
        break;
      }
      case 'find': {
        this.goToFind();
        break;
      }
      case 'goTo': {
        this.goTo();
        break;
      }
      case 'documentation': {
        this.openDocumentation();
        break;
      }
      case 'todos': {
        this.openTodos();
        break;
      }
    }
  }

  private isMac(): boolean {
    const nav = navigator as Navigator & { userAgentData?: { platform?: string } };

    if (nav.userAgentData?.platform) {
      return nav.userAgentData.platform.toLowerCase().includes('mac');
    }

    return navigator.userAgent.toLowerCase().includes('mac');
  }
}
